import * as React from 'react'

import { Box, Href, Spinner, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
  JobCatalogTreeFragment,
  TaskDetailAffecteeEmployeeFragment,
  TaskDetailAffecteeReferenceContractFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import TextInput from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { translations as roleLevelSalaryTranslations } from 'modules/contracts/components/FixFlow/lib/roleLevelSalaryTranslations'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import { useLegalEntityRoles } from 'modules/contracts/hooks/useLegalEntityRoles'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import { translate } from 'modules/contracts/lib/translate'
import useCreateRole from 'modules/employees/components/UnifiedAddEmployeeModal/ContractInformationStep/useCreateRole'
import JobCatalogTreeSelector from 'modules/job_catalog/components/JobCatalogTreeSelector'
import useCreateLevel from 'modules/job_catalog/hooks/useCreateLevel'
import canSeeIntegration from 'modules/marketplace/lib/canSeeIntegration'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

import A3innuvaJobPosition from '../_A3innuvaJobPosition'
import LevelSelect from './LevelSelect'
import RolesSelect from './RolesSelect'

interface Props {
  withFormWrapper?: boolean
  form: Form
  translations?: { [key: string]: string }
  type?: ContractFlowType
  withSeparator?: boolean
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  canSeeJobTitle: boolean
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
  contractVersion?:
    | ContractsEmployeesReferenceContractFragment
    | ContractsEmployeesContractVersionFragment
    | TaskDetailAffecteeReferenceContractFragment
    | null
  employee?: ContractsEmployeesEmployeeFragment | TaskDetailAffecteeEmployeeFragment | null
}

interface RoleHintProps {
  isOpenRoleSelector: boolean
}

const RoleHint = ({ isOpenRoleSelector }: RoleHintProps) => {
  return (
    <Hint color="viridian100" visible={isOpenRoleSelector}>
      <Text.Normal color="grey1000" weight="normal" size="100">
        {i18n.t('contract_versions.flows.fix.steps.role_level_salary.hints.role')}
      </Text.Normal>
    </Hint>
  )
}

interface LevelHintProps {
  isRoleSelected: boolean
  roleHasLevels?: boolean
  isLevelSelected: boolean
}

const LevelHint = ({ isRoleSelected, roleHasLevels, isLevelSelected }: LevelHintProps) => {
  if (isRoleSelected && !roleHasLevels) {
    return (
      <Hint color="viridian100">
        <Text.Normal color="grey1000" weight="normal" size="100">
          {i18n.t('contract_versions.flows.fix.steps.role_level_salary.hints.level_empty')}
        </Text.Normal>
      </Hint>
    )
  }

  return (
    <Hint color="champagne100" visible={isRoleSelected && !isLevelSelected}>
      <Text.Normal color="grey1000" weight="normal" size="100">
        {i18n.t('contract_versions.flows.fix.steps.role_level_salary.hints.level_missing')}
      </Text.Normal>
    </Hint>
  )
}

const RolesMappingHint = () => {
  return (
    <Hint color="grey200">
      <Text.Normal color="grey1000" weight="normal" size="100">
        {i18n.t(
          'contract_versions.flows.fix.steps.role_level_salary.hints.roles_mapping_description'
        )}
      </Text.Normal>
      <Href
        href={
          'https://help.factorialhr.com/payroll-integrations/about-a3innuva-nomina-integration?from_search=173870120#:~:text=Install%20for%20company-,2.%20Configure%20a3innuva%20N%C3%B3mina,-After%20installing%20the'
        }
        target="_blank"
      >
        <Text.Normal color="grey1000" weight="normal" size="100" underlined>
          {i18n.t('contract_versions.flows.fix.steps.role_level_salary.hints.roles_mapping_link')}
        </Text.Normal>
      </Href>
    </Hint>
  )
}

const RoleLevel = ({
  withFormWrapper,
  form,
  withSeparator = true,
  translations = roleLevelSalaryTranslations(),
  type = 'fix',
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  canSeeJobTitle,
  contractVersion,
  employee,
  jobCatalogContextlessPolicies,
}: Props) => {
  const legalEntityId = employee?.legalEntity?.id
  const canSeeJobCatalog = jobCatalogContextlessPolicies?.seeJobCatalog.can ?? false
  const canCreateJob = jobCatalogContextlessPolicies?.createJob.can ?? false
  const { spanishContractData } = useLegalEntityContractData()
  const [rolesCollection, isLoadingRoles, refetchRoles] = useLegalEntityRoles(legalEntityId ?? 0)
  const [createRole] = useCreateRole()
  const createLevel = useCreateLevel()
  const canSeeA3InnuvaIntegration = Boolean(
    canSeeIntegration(integrationCodes.a3innuva, spanishContractData.companyInstallations)
  )

  const canSeeA3innuvaJobPositionField =
    canSeeA3InnuvaIntegration && form.has('es_a3innuva_job_position_id')

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const [isOpenRoleSelector, setIsOpenRoleSelector] = React.useState(false)
  const [isOpenLevelSelector, setIsOpenLevelSelector] = React.useState(false)

  const roleId = Number(form.get('job_catalog_role_id').value)
  const role = rolesCollection && roleId ? rolesCollection[+roleId] : undefined
  const levels = role && rolesCollection ? rolesCollection[role.id]?.levels : undefined
  const levelId = Number(form.get('job_catalog_level_id').value)
  const roleHasLevels = levels && levels.filter((level) => !level.isDefault).length > 0
  const isRoleSelected = form.get('job_catalog_role_id').value
  const isLevelSelected = form.get('job_catalog_level_id').value

  if (!isLevelSelected) {
    const defaultLevel = levels?.find((level) => level.isDefault)?.id

    if (defaultLevel) {
      form.setValues({
        job_catalog_level_id: defaultLevel,
      })
    }
  }

  const legalEntity = employee?.legalEntity
  const handleCreateRole = async (name: string) => {
    if (legalEntity) {
      const result = await createRole(name, currentCompanyId, legalEntity.id)
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        refetchRoles()
      }
    }
  }

  const handleCreateLevel = async (name: string) => {
    if (role) {
      const result = await createLevel(name, role.id)
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        refetchRoles()
      }
    }
  }

  const onRoleSelect = (value: number | undefined) => {
    // We need to reset the level ID as at this point the levels loaded in memory belongs to the previous role
    form.setValues({
      job_catalog_role_id: value,
      job_catalog_level_id: undefined,
    })

    setIsOpenLevelSelector(true)
    setIsOpenRoleSelector(false)
  }

  const onToggleLevelSelector = (isOpen: boolean) => {
    if (isOpen) {
      if (isRoleSelected && (roleHasLevels || canCreateJob)) setIsOpenLevelSelector(true)
      return
    }

    setIsOpenLevelSelector(false)
  }

  const onToggleRoleSelector = (isOpen: boolean) => {
    setIsOpenRoleSelector(isOpen)
  }

  const onLevelSelect = (value: number | undefined) => {
    form.setValues({ job_catalog_level_id: value })

    if (value) {
      setIsOpenLevelSelector(false)
      return
    }

    if (isRoleSelected && roleHasLevels) {
      setIsOpenLevelSelector(true)
      return
    }

    form.setValues({ job_catalog_role_id: null })
  }

  const onJobCatalogTreeSelect = (
    jobCatalogTreeNodeUuid: string | undefined,
    jobCatalogTreeNodeName: string | undefined
  ) => {
    form.get('job_catalog_role_level_name').set(jobCatalogTreeNodeName)
    form.get('job_catalog_tree_node_uuid').set(jobCatalogTreeNodeUuid)
  }

  const noLevels = isRoleSelected && !roleHasLevels

  const isSpanish =
    contractVersion && type === 'fix'
      ? contractVersion.country === 'es'
      : employee?.legalEntity?.country === 'es'

  if (isLoadingRoles)
    return (
      <Box flexGrow justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Box gap="s24">
      {canSeeJobCatalog && !jobCatalogTreeFeatureEnabled && (
        <FormWrapper
          withFormWrapper={withFormWrapper}
          hint={<RoleHint isOpenRoleSelector={isOpenRoleSelector} />}
        >
          <RolesSelect
            onRoleSelect={onRoleSelect}
            selectedRole={roleId}
            roles={Object.values(rolesCollection || {})}
            createJob={handleCreateRole}
            onToggle={onToggleRoleSelector}
            isOpen={isOpenRoleSelector}
            canCreate={canCreateJob}
          />
        </FormWrapper>
      )}
      {jobCatalogTreeFeatureEnabled && (
        <FormWrapper withFormWrapper={withFormWrapper}>
          <JobCatalogTreeSelector
            serializedTree={jobCatalogTrees?.[0]?.serializedTree || ''}
            onLevelSelect={onJobCatalogTreeSelect}
            selectedLevelId={form.get('job_catalog_tree_node_uuid').value}
          />
        </FormWrapper>
      )}
      {canSeeJobCatalog && !jobCatalogTreeFeatureEnabled && (
        <FormWrapper
          withFormWrapper={withFormWrapper}
          hint={
            <LevelHint
              isRoleSelected={isRoleSelected}
              roleHasLevels={roleHasLevels}
              isLevelSelected={isLevelSelected}
            />
          }
        >
          <LevelSelect
            onLevelSelect={onLevelSelect}
            selectedLevel={levelId}
            levels={roleHasLevels ? levels : []}
            disabled={!isRoleSelected || (!roleHasLevels && !canCreateJob)}
            isOpen={isOpenLevelSelector}
            onToggle={onToggleLevelSelector}
            noLevels={noLevels}
            createLevel={handleCreateLevel}
            mandatory={isRoleSelected && !!roleHasLevels && !isLevelSelected}
            canCreate={canCreateJob}
          />
        </FormWrapper>
      )}
      {canSeeJobTitle && (
        <FormWrapper withFormWrapper={withFormWrapper}>
          <TextInput
            secondaryLabel={
              <InformationTooltip
                information={translate(translations, 'job_title_tooltip')}
                icon="question"
              />
            }
            field={form.get('job_title')}
            label={translate(translations, 'job_title')}
          />
        </FormWrapper>
      )}
      {isSpanish && (
        <FormWrapper withFormWrapper={withFormWrapper}>
          <TextArea
            field={form.get('es_job_description')}
            label={translate(translations, 'job_description')}
          />
        </FormWrapper>
      )}
      {canSeeA3innuvaJobPositionField && legalEntityId && (
        <FormWrapper hint={<RolesMappingHint />}>
          <A3innuvaJobPosition
            form={form}
            translations={translations}
            spanishContractData={spanishContractData}
            legalEntityId={legalEntityId}
          />
        </FormWrapper>
      )}
      {withSeparator && (
        <FormWrapper withFormWrapper={withFormWrapper}>
          <Box
            paddingBottom="s24"
            border={{
              bottom: {
                width: 's2',
                color: 'grey400',
                style: 'solid',
              },
            }}
          />
        </FormWrapper>
      )}
    </Box>
  )
}

export default observer(RoleLevel)
